import { Component, OnInit } from '@angular/core';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Swal from 'sweetalert2';
import {
  AsignacionCampana,
  BonoAdminItem,
  CampanaReferidos,
  ConfigReferidos,
  CrearCampanaReferidos,
  FiltrosBonos,
  InversionRetorno,
  LlaveService,
  OpcionesCampana,
  Pagina,
  RendimientoCampana,
  ResumenAdmin,
  TableroCampana,
  TopReferidor,
  UsuarioConBono,
  UsuarioConLlave,
} from 'src/app/shared/services/llave.service';

/** Las seis vistas del panel. */
type Vista =
  | 'inversion'
  | 'campanas'
  | 'vinculos'
  | 'usuarios'
  | 'ganancias'
  | 'top';

/**
 * El panel del programa de referidos, para operaciones.
 *
 * Seis vistas sobre la misma cosa, de lo general al detalle:
 *
 *   1. **Inversión y retorno** — la pregunta de socio: cuánto se puso, cuánto
 *      volvió y si esto se sostiene. Es la primera a propósito.
 *   2. **Campañas** — los acuerdos: la global que alcanza a todos y las
 *      asignadas con recompensa mejorada. Se crean, activan, pausan y cierran
 *      desde acá, y cada una abre su tablero de riesgo, fuga y retorno.
 *   3. **Vínculos** — quién invitó a quién y en qué estado quedó cada bono.
 *   4. **Registrados con llave** — todos los que entraron por un link,
 *      incluidos los que no terminaron el alta.
 *   5. **Ganancias** — la misma gente vista por persona y no por vínculo:
 *      cuánto cobró, cuánto espera y por qué. Una persona puede tener bonos de
 *      los dos lados y en campañas distintas, y ésta es la única vista que los
 *      suma.
 *   6. **Rendimiento** — qué rindió cada campaña y quiénes traen más.
 *
 * El número que hay que mirar antes de subir un monto es `retenidoUsdt`: bonos
 * prometidos que se van a pagar si esos invitados llegan a cargar. Sin él el
 * gasto del mes engaña — se ve bajo justo antes de que una tanda fondee junta.
 *
 * **Todos los listados van paginados contra el servidor.** Traer todo y paginar
 * en el cliente es la mitad del trabajo y ninguna de las ventajas: la pantalla
 * deja de cargar el día que hay unos miles de vínculos, sin aviso previo.
 */
@Component({
  selector: 'app-referidos-admin',
  templateUrl: './referidos-admin.component.html',
  styleUrls: ['./referidos-admin.component.sass'],
})
export class ReferidosAdminComponent implements OnInit {
  cargando = true;
  cargandoTabla = false;
  error = '';

  resumen: ResumenAdmin | null = null;
  bonos: BonoAdminItem[] = [];
  top: TopReferidor[] = [];

  page = 1;
  limit = 20;
  total = 0;

  // Arranca en inversión y retorno: es la pregunta que se hace primero al
  // abrir el panel —«¿esto se sostiene?»— y de ahí se baja al detalle.
  vista: Vista = 'inversion';

  /** Filtros de la tabla de vínculos. Vacío = sin filtrar. */
  filtroEstado = '';
  filtroBono = '';
  busqueda = '';

  private selloBusqueda = 0;

  // --- campañas ---
  campanas: CampanaReferidos[] = [];
  campanasPage = 1;
  campanasTotal = 0;
  filtroCampanaEstado = '';
  filtroCampanaTipo = '';

  /** La campaña abierta en el panel de detalle, con su tablero. */
  campanaAbierta: CampanaReferidos | null = null;
  tablero: TableroCampana | null = null;
  cargandoTablero = false;
  asignaciones: AsignacionCampana[] = [];
  asignacionesPage = 1;
  asignacionesTotal = 0;

  // --- alta y edición de campaña ---
  mostrarAlta = false;
  guardandoAlta = false;
  opciones: OpcionesCampana | null = null;
  nueva: CrearCampanaReferidos = this.campanaEnBlanco();
  /** La campaña que se está editando, o `null` si es un alta. */
  editando: CampanaReferidos | null = null;

  // --- asignar referidores ---
  mostrarAsignar = false;
  asignando = false;
  referidoresTexto = '';
  cuotaAsignada: number | null = null;
  asignacionExclusiva = false;

  // --- registrados con llave ---
  usuarios: UsuarioConLlave[] = [];
  usuariosPage = 1;
  usuariosTotal = 0;
  filtroReclamo = '';
  busquedaUsuarios = '';
  private selloUsuarios = 0;

  // --- inversión y retorno ---
  inversion: InversionRetorno | null = null;
  inversionPage = 1;

  // --- ganancias por persona ---
  ganadores: UsuarioConBono[] = [];
  ganadoresPage = 1;
  ganadoresTotal = 0;
  filtroGanancia = '';
  busquedaGanadores = '';
  private selloGanadores = 0;

  // --- rendimiento ---
  rendimiento: RendimientoCampana[] = [];
  topPage = 1;
  topTotal = 0;

  // --- ayuda ---
  /**
   * El modal que explica cómo funciona esto.
   *
   * No es decoración: el módulo tiene una docena de decisiones que no se
   * adivinan mirando el formulario —que los cupos van en pares, que todo se
   * congela al crear el vínculo, que sólo puede haber una global— y cada una
   * mal entendida cuesta plata o cuesta un cliente. Vale más tenerlas a un clic
   * que en un documento que nadie abre.
   */
  mostrarAyuda = false;
  /** Qué parte se está mirando: la configuración general o el alta. */
  ayudaSeccion: 'programa' | 'campana' = 'programa';

  // --- descargas ---
  /** Qué tabla se está bajando, para deshabilitar sólo ese botón. */
  descargando = '';

  // --- configuración ---
  mostrarConfig = false;
  guardandoConfig = false;
  /** Copia editable. Se trabaja sobre una copia para poder cancelar sin efecto. */
  config: ConfigReferidos | null = null;
  configOriginal: ConfigReferidos | null = null;

  constructor(private llaveService: LlaveService) {}

  /**
   * Un total del resumen por moneda: «10 USDT · 300 Bs.». Sin `porMoneda`
   * (API anterior) cae al número viejo, que el API suma en USDT.
   */
  totalResumen(campo: 'liberado' | 'retenido' | 'liberadoEsteMes'): string {
    const r = this.resumen;
    if (!r) return '';
    if (!r.porMoneda) {
      const viejo =
        campo === 'liberado'
          ? r.liberadoUsdt
          : campo === 'retenido'
            ? r.retenidoUsdt
            : r.liberadoEsteMesUsdt;
      return `${viejo} USDT`;
    }
    const partes = r.porMoneda
      .filter((m) => Number(m[campo]) > 0)
      .map((m) => this.llaveService.montoTexto(Number(m[campo]), m.moneda));
    return partes.length ? partes.join(' · ') : '0';
  }

  async ngOnInit(): Promise<void> {
    await this.cargar();
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.total / this.limit));
  }

  get campanasPaginas(): number {
    return Math.max(1, Math.ceil(this.campanasTotal / this.limit));
  }

  get usuariosPaginas(): number {
    return Math.max(1, Math.ceil(this.usuariosTotal / this.limit));
  }

  get topPaginas(): number {
    return Math.max(1, Math.ceil(this.topTotal / this.limit));
  }

  get inversionPaginas(): number {
    return Math.max(1, Math.ceil((this.inversion?.total ?? 0) / this.limit));
  }

  get ganadoresPaginas(): number {
    return Math.max(1, Math.ceil(this.ganadoresTotal / this.limit));
  }

  get vinculados(): number {
    return this.resumen?.vinculos?.['VINCULADO'] ?? 0;
  }

  get esperandoAlta(): number {
    return this.resumen?.vinculos?.['ESPERANDO_ALTA'] ?? 0;
  }

  get anulados(): number {
    return this.resumen?.vinculos?.['ANULADO'] ?? 0;
  }

  private async cargar(): Promise<void> {
    this.cargando = true;
    this.error = '';
    try {
      const [resumen, campanas, inversion] = await Promise.all([
        this.llaveService.adminResumen(),
        this.llaveService.campanas({ page: 1, limit: this.limit }),
        this.llaveService.inversionRetorno(1, this.limit),
      ]);
      this.resumen = resumen;
      this.campanas = campanas.items;
      this.campanasTotal = campanas.total;
      this.campanasPage = campanas.page;
      this.inversion = inversion;
      this.inversionPage = inversion.page;
    } catch (e: any) {
      this.error =
        e?.error?.message || 'No pudimos cargar el tablero. Recargá la página.';
    } finally {
      this.cargando = false;
    }
  }

  // -------------------------------------------------------------- pestañas

  async verVista(v: Vista): Promise<void> {
    this.vista = v;
    if (v === 'vinculos' && !this.bonos.length) await this.traer(1);
    if (v === 'usuarios' && !this.usuarios.length) await this.traerUsuarios(1);
    if (v === 'top' && !this.top.length) await this.traerTop(1);
    if (v === 'ganancias' && !this.ganadores.length) await this.traerGanadores(1);
    if (v === 'inversion' && !this.inversion) await this.traerInversion(1);
  }

  // -------------------------------------------------- inversión y retorno

  async traerInversion(page: number): Promise<void> {
    if (page < 1 || page > this.inversionPaginas) return;
    this.cargandoTabla = true;
    try {
      this.inversion = await this.llaveService.inversionRetorno(page, this.limit);
      this.inversionPage = this.inversion.page;
    } finally {
      this.cargandoTabla = false;
    }
  }

  // ----------------------------------------------------------- ganancias

  async traerGanadores(page: number): Promise<void> {
    if (page < 1) return;
    this.cargandoTabla = true;
    try {
      const r = await this.llaveService.usuariosConBono({
        page,
        limit: this.limit,
        q: this.busquedaGanadores || undefined,
        estado: this.filtroGanancia || undefined,
      });
      this.ganadores = r.items;
      this.ganadoresTotal = r.total;
      this.ganadoresPage = r.page;
    } catch {
      this.ganadores = [];
      this.ganadoresTotal = 0;
    } finally {
      this.cargandoTabla = false;
    }
  }

  async onBuscarGanadores(): Promise<void> {
    const sello = ++this.selloGanadores;
    await new Promise((r) => setTimeout(r, 400));
    if (sello !== this.selloGanadores) return;
    await this.traerGanadores(1);
  }

  async filtrarGanadores(): Promise<void> {
    await this.traerGanadores(1);
  }

  // -------------------------------------------------------------- campañas

  async traerCampanas(page: number): Promise<void> {
    if (page < 1 || page > this.campanasPaginas) return;
    this.cargandoTabla = true;
    try {
      const r = await this.llaveService.campanas({
        page,
        limit: this.limit,
        estado: this.filtroCampanaEstado || undefined,
        tipo: this.filtroCampanaTipo || undefined,
      });
      this.campanas = r.items;
      this.campanasTotal = r.total;
      this.campanasPage = r.page;
    } catch {
      this.campanas = [];
      this.campanasTotal = 0;
    } finally {
      this.cargandoTabla = false;
    }
  }

  /** Al cambiar un filtro se vuelve a la página 1: la 7 puede no existir más. */
  async filtrarCampanas(): Promise<void> {
    this.campanasPage = 1;
    await this.traerCampanas(1);
  }

  /**
   * Abre el detalle de una campaña: su tablero y sus referidores.
   *
   * Las dos consultas van juntas porque la pregunta del operador es una sola
   * —«¿cómo va este acuerdo?»— y verlas aparecer en dos momentos distintos
   * hace parecer que la pantalla se colgó.
   */
  async abrirCampana(c: CampanaReferidos): Promise<void> {
    this.campanaAbierta = c;
    this.tablero = null;
    this.asignaciones = [];
    this.cargandoTablero = true;
    // Van por separado y no en un Promise.all: son dos preguntas distintas y
    // que falle una no tiene por qué borrar la otra. Con las dos juntas, un
    // tablero caído dejaba la lista de asignados vacía y sin explicación.
    const [tablero, asignaciones] = await Promise.allSettled([
      this.llaveService.tableroCampana(c.referidoCampanaId),
      c.tipo === 'ASIGNADA'
        ? this.llaveService.asignaciones(c.referidoCampanaId, 1, this.limit)
        : Promise.resolve({ items: [], total: 0, page: 1, limit: this.limit }),
    ]);

    this.tablero = tablero.status === 'fulfilled' ? tablero.value : null;

    if (asignaciones.status === 'fulfilled') {
      this.asignaciones = asignaciones.value.items;
      this.asignacionesTotal = asignaciones.value.total;
      this.asignacionesPage = asignaciones.value.page;
    }

    this.cargandoTablero = false;
  }

  cerrarCampana(): void {
    this.campanaAbierta = null;
    this.tablero = null;
    this.asignaciones = [];
  }

  async irAPaginaAsignaciones(page: number): Promise<void> {
    if (!this.campanaAbierta) return;
    const paginas = Math.max(1, Math.ceil(this.asignacionesTotal / this.limit));
    if (page < 1 || page > paginas) return;
    const r = await this.llaveService.asignaciones(
      this.campanaAbierta.referidoCampanaId,
      page,
      this.limit,
    );
    this.asignaciones = r.items;
    this.asignacionesTotal = r.total;
    this.asignacionesPage = r.page;
  }

  /**
   * Activar, pausar, reanudar o cerrar.
   *
   * Cada una se confirma con el texto de lo que hace de verdad. Activar y
   * cerrar son las que no se deshacen: activar arranca los pagos y cerrar
   * termina el acuerdo, y una campaña cerrada no vuelve a abrirse.
   */
  async accion(
    c: CampanaReferidos,
    accion: 'activar' | 'pausar' | 'reanudar' | 'cerrar',
  ): Promise<void> {
    const textos: Record<string, { title: string; html: string; btn: string }> = {
      activar: {
        title: `¿Activar ${c.codigo}?`,
        html:
          'Desde este momento los links de sus referidores enganchan vínculos y ' +
          'la bolsa queda comprometida.<br><br>' +
          'Si la bolsa pasa el umbral de doble control, la tiene que activar ' +
          '<b>otro operador</b>: quien crea la campaña no la activa.',
        btn: 'Activar',
      },
      pausar: {
        title: `¿Pausar ${c.codigo}?`,
        html:
          'No engancha vínculos nuevos. Los ya creados <b>mantienen su campaña y ' +
          'sus montos</b> y cobran si cumplen.<br><br>' +
          'Sus referidores pasan mientras tanto a la campaña global: la pausa ' +
          'frena la campaña mejorada, no el programa.',
        btn: 'Pausar',
      },
      reanudar: {
        title: `¿Reanudar ${c.codigo}?`,
        html:
          c.pausadaMotivo === 'VELOCIDAD'
            ? 'La pausó <b>el freno de velocidad</b>, así que la tiene que ' +
              'reanudar otro operador. Antes de hacerlo conviene mirar por qué ' +
              'se tomaron tantos cupos tan rápido.'
            : 'Vuelve a enganchar vínculos nuevos.',
        btn: 'Reanudar',
      },
      cerrar: {
        title: `¿Cerrar ${c.codigo}?`,
        html:
          'Termina el acuerdo. <b>No vuelve a abrirse</b> — para eso se crea ' +
          'otra.<br><br>' +
          'Los bonos ya retenidos se respetan: se liberan si cumplen su ' +
          'condición antes de vencer.',
        btn: 'Cerrar',
      },
    };

    const t = textos[accion];
    const ok = await Swal.fire({
      title: t.title,
      html: t.html,
      icon: accion === 'cerrar' ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonText: t.btn,
      cancelButtonText: 'Volver',
      confirmButtonColor: '#2F4A2B',
    });
    if (!ok.isConfirmed) return;

    try {
      await this.llaveService.accionCampana(c.referidoCampanaId, accion);
      await this.traerCampanas(this.campanasPage);
      if (this.campanaAbierta?.referidoCampanaId === c.referidoCampanaId) {
        const actualizada = this.campanas.find(
          (x) => x.referidoCampanaId === c.referidoCampanaId,
        );
        if (actualizada) this.campanaAbierta = actualizada;
      }
    } catch (e: any) {
      // El backend explica el doble control, la global duplicada y los estados
      // que no permiten la acción. Ese texto es el que sirve, no uno genérico.
      const msg = e?.error?.message;
      Swal.fire({
        title: 'No se pudo',
        text: Array.isArray(msg) ? msg.join(' ') : msg || 'Intentalo de nuevo.',
        icon: 'error',
      });
    }
  }

  // ---------------------------------------------------------- alta de campaña

  /**
   * Los defaults salen del relevamiento de producción, no de la nada: 20.000
   * CLP / 20.000 VES / 20 USDT quedan parejos en valor, y el más bajo sería
   * siempre la vía barata.
   */
  private campanaEnBlanco(): CrearCampanaReferidos {
    const hoy = new Date();
    const enTresMeses = new Date(hoy.getTime() + 90 * 24 * 60 * 60 * 1000);
    return {
      nombre: '',
      codigo: '',
      // Sólo globales: una campaña por país.
      tipo: 'GLOBAL',
      prioridad: 999,
      alcanceCodigo: 'CL',
      cuotaPorReferidor: 20,
      gatilloReferidor: 'AL_FONDEAR',
      actividadReferidorDias: 90,
      pisoFondeoClp: 20000,
      pisoFondeoVes: 20000,
      pisoFondeoUsdt: 20,
      moneda: 'USDT',
      montoReferidor: 5,
      montoReferido: 5,
      unidadesTotal: 100,
      vigenciaDesde: hoy.toISOString().slice(0, 10),
      vigenciaHasta: enTresMeses.toISOString().slice(0, 10),
      reservaDias: 30,
      pausaPct: 20,
      pausaMinutos: 60,
      acumulaConBonos: false,
    };
  }

  async abrirAlta(): Promise<void> {
    this.editando = null;
    this.nueva = this.campanaEnBlanco();
    await this.cargarOpciones();
    this.mostrarAlta = true;
  }

  /**
   * Abre el mismo formulario en modo edición.
   *
   * Se reusa el del alta a propósito: son los mismos catorce ejes y tener dos
   * pantallas para lo mismo es cómo terminan diciendo cosas distintas. Lo que
   * cambia es qué se puede tocar, y eso lo decide `bloqueado()`.
   */
  async abrirEdicion(c: CampanaReferidos): Promise<void> {
    this.editando = c;
    this.nueva = {
      nombre: c.nombre,
      codigo: c.codigo,
      tipo: c.tipo,
      prioridad: c.prioridad,
      alcanceCodigo: c.alcanceCodigo,
      cuotaPorReferidor: c.cuotaPorReferidor,
      gatilloReferidor: c.gatilloReferidor,
      actividadReferidorDias: c.actividadReferidorDias,
      // Sin esto los tres campos abrían vacíos y la pantalla mentía: la campaña
      // sí tiene pisos —el alta los pone en 20000/20000/20 si no se tocan— y el
      // operador no tenía cómo ver con cuáles quedó.
      pisoFondeoClp: this.pisoDeVista(c.pisoFondeoClp),
      pisoFondeoVes: this.pisoDeVista(c.pisoFondeoVes),
      pisoFondeoUsdt: this.pisoDeVista(c.pisoFondeoUsdt),
      moneda: c.moneda,
      montoReferidor: Number(c.montoReferidor),
      montoReferido: Number(c.montoReferido),
      unidadesTotal: c.unidadesTotal,
      vigenciaDesde: String(c.vigenciaDesde).slice(0, 10),
      vigenciaHasta: c.vigenciaHasta ? String(c.vigenciaHasta).slice(0, 10) : undefined,
      reservaDias: c.reservaDias,
      pausaPct: c.pausaPct,
      pausaMinutos: c.pausaMinutos,
      acumulaConBonos: c.acumulaConBonos,
    };
    await this.cargarOpciones();
    this.mostrarAlta = true;
  }

  /**
   * El piso tal como lo manda el API, listo para el input.
   *
   * `undefined` deja el campo vacío y `guardarCampana` no lo manda, que es lo
   * que corresponde con un API viejo que todavía no devuelve los pisos:
   * mandar un NaN viajaría como null y le borraría el piso a la campaña.
   */
  private pisoDeVista(valor: string | null | undefined): number | undefined {
    if (valor === null || valor === undefined) return undefined;
    const n = Number(valor);
    return Number.isFinite(n) ? n : undefined;
  }

  private async cargarOpciones(): Promise<void> {
    try {
      this.opciones = await this.llaveService.campanaOpciones();
    } catch {
      this.opciones = null;
    }
  }

  cerrarAlta(): void {
    this.mostrarAlta = false;
    this.editando = null;
  }

  /** `true` si la campaña que se edita ya está viva. */
  get editandoViva(): boolean {
    return Boolean(this.editando && this.editando.estado !== 'BORRADOR');
  }

  /**
   * Qué campos no se pueden tocar en este momento.
   *
   * El código y el tipo nunca: el código es la identidad de la campaña en todos
   * los reportes, y mover el tipo con vínculos enganchados los deja apuntando a
   * algo que dejó de ser lo que era.
   *
   * La moneda y los montos, sólo mientras la campaña esté viva. No es por los
   * vínculos viejos —tienen su monto congelado— sino por el doble control:
   * crear una campaña chica, hacerla aprobar y después subirle el monto sería
   * la forma más limpia de sacar plata sin que ninguna revisión lo note.
   *
   * El backend lo rechaza igual; esto es para que el operador lo vea antes de
   * escribir, no para reemplazar el control.
   */
  bloqueado(campo: string): boolean {
    if (!this.editando) return false;
    if (campo === 'codigo' || campo === 'tipo') return true;
    if (!this.editandoViva) return false;
    // El país tampoco: cambiarlo le cambia las reglas a quien ya la veía.
    return ['moneda', 'montoReferidor', 'montoReferido', 'vigenciaDesde', 'alcanceCodigo'].includes(
      campo,
    );
  }

  /** Las monedas que ese alcance admite Y tienen camino de acreditación. */
  get monedasDelAlcance(): string[] {
    if (!this.opciones) return ['USDT'];
    const alcance = this.opciones.alcances.find(
      (a) => a.alcance === this.nueva.alcanceCodigo,
    );
    if (!alcance) return ['USDT'];
    return alcance.monedas.filter((m) =>
      this.opciones!.monedasAcreditables.includes(m),
    );
  }

  /** La bolsa que la campaña compromete, para verla antes de guardar. */
  get bolsaEstimada(): number {
    const porPar =
      Number(this.nueva.montoReferidor || 0) + Number(this.nueva.montoReferido || 0);
    return porPar * Number(this.nueva.unidadesTotal || 0);
  }

  /** `AL_KYC` paga por completar un registro, no por operar: exige frenos. */
  get avisoKyc(): string | null {
    if (this.nueva.gatilloReferidor !== 'AL_KYC') return null;
    const faltan: string[] = [];
    if ((this.nueva.unidadesTotal ?? 0) > 100) faltan.push('bolsa de hasta 100 cupos');
    if (!this.nueva.cuotaPorReferidor || this.nueva.cuotaPorReferidor > 3) {
      faltan.push('cuota de hasta 3 por referidor');
    }
    if (!this.nueva.pausaPct || !this.nueva.pausaMinutos) {
      faltan.push('freno de velocidad');
    }
    return faltan.length
      ? `Con este gatillo el bono se paga por completar un registro: falta ${faltan.join(', ')}.`
      : null;
  }

  async guardarCampana(): Promise<void> {
    if (this.guardandoAlta) return;
    this.guardandoAlta = true;
    try {
      const dto: any = { ...this.nueva };
      // La global no lleva fecha de fin ni prioridad propia: es la red de abajo
      // y va siempre última.
      if (dto.tipo === 'GLOBAL') {
        delete dto.vigenciaHasta;
        delete dto.prioridad;
      }

      if (this.editando) {
        // No se mandan los campos que el backend va a rechazar: mandarlos haría
        // que una edición inofensiva —cambiar el piso— vuelva con un 403 por un
        // campo que el operador ni tocó.
        for (const campo of ['codigo', 'tipo', 'moneda', 'montoReferidor',
                             'montoReferido', 'vigenciaDesde', 'alcanceCodigo']) {
          if (this.bloqueado(campo)) delete dto[campo];
        }
        await this.llaveService.editarCampana(this.editando.referidoCampanaId, dto);
        this.cerrarAlta();
        await this.traerCampanas(this.campanasPage);
        Swal.fire({
          title: 'Campaña actualizada',
          html: this.editandoViva
            ? 'Los vínculos ya creados <b>mantienen lo que se les prometió</b>: ' +
              'lo nuevo aplica de acá en adelante.'
            : 'Sigue en borrador: no engancha a nadie hasta que se active.',
          icon: 'success',
          confirmButtonColor: '#2F4A2B',
        });
        return;
      }

      await this.llaveService.crearCampana(dto);
      this.cerrarAlta();
      await this.traerCampanas(1);
      Swal.fire({
        title: 'Campaña creada',
        html:
          'Nace en <b>BORRADOR</b>: no engancha vínculos ni compromete ' +
          'presupuesto hasta que alguien la active.',
        icon: 'success',
        confirmButtonColor: '#2F4A2B',
      });
    } catch (e: any) {
      const msg = e?.error?.message;
      Swal.fire({
        title: this.editando ? 'No se pudo guardar' : 'No se pudo crear',
        text: Array.isArray(msg) ? msg.join(' ') : msg || 'Revisá los valores.',
        icon: 'error',
      });
    } finally {
      this.guardandoAlta = false;
    }
  }

  // ------------------------------------------------------------- asignaciones

  abrirAsignar(): void {
    this.referidoresTexto = '';
    this.cuotaAsignada = null;
    this.asignacionExclusiva = false;
    this.mostrarAsignar = true;
  }

  cerrarAsignar(): void {
    this.mostrarAsignar = false;
  }

  async asignar(): Promise<void> {
    if (!this.campanaAbierta || this.asignando) return;

    // Una llave o un correo por línea, o separados por coma: el operador los
    // pega de donde los tenga y no debería tener que formatearlos.
    const referidores = this.referidoresTexto
      .split(/[\n,;]+/)
      .map((x) => x.trim())
      .filter(Boolean);

    if (!referidores.length) return;

    this.asignando = true;
    try {
      const r = await this.llaveService.asignarReferidores(
        this.campanaAbierta.referidoCampanaId,
        {
          referidores,
          cuota: this.cuotaAsignada ?? undefined,
          exclusiva: this.asignacionExclusiva,
        },
      );
      this.cerrarAsignar();
      await this.abrirCampana(this.campanaAbierta);

      // Los omitidos se muestran con el motivo: asignarle recompensa a quien
      // compliance no aprobó es prometerle algo que nunca va a poder cobrar, y
      // enterarse acá es lo que permite arreglarlo.
      const omitidos = r.omitidos
        .map((o) => `<b>${o.referencia}</b>: ${o.motivo}`)
        .join('<br>');
      Swal.fire({
        title: `${r.asignados} asignado(s)`,
        html: omitidos ? `No se pudo con:<br>${omitidos}` : 'Todos quedaron asignados.',
        icon: r.omitidos.length ? 'warning' : 'success',
        confirmButtonColor: '#2F4A2B',
      });
    } catch (e: any) {
      const msg = e?.error?.message;
      Swal.fire({
        title: 'No se pudo asignar',
        text: Array.isArray(msg) ? msg.join(' ') : msg || 'Intentalo de nuevo.',
        icon: 'error',
      });
    } finally {
      this.asignando = false;
    }
  }

  async desasignar(a: AsignacionCampana): Promise<void> {
    if (!this.campanaAbierta) return;

    const ok = await Swal.fire({
      title: `¿Sacarle el link mejorado a ${a.nombre}?`,
      html:
        'Sus vínculos ya creados <b>mantienen su campaña y sus montos</b>: lo ' +
        'prometido no se toca.<br><br>' +
        'Sus links nuevos vuelven a la campaña global.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sacar',
      cancelButtonText: 'Volver',
      confirmButtonColor: '#2F4A2B',
    });
    if (!ok.isConfirmed) return;

    await this.llaveService.desasignarReferidor(
      this.campanaAbierta.referidoCampanaId,
      a.usuarioId,
    );
    await this.irAPaginaAsignaciones(this.asignacionesPage);
  }

  // ----------------------------------------------------------------- vínculos

  private filtros(page: number): FiltrosBonos {
    return {
      page,
      limit: this.limit,
      estado: this.filtroEstado || undefined,
      bono: this.filtroBono || undefined,
      q: this.busqueda || undefined,
    };
  }

  async irAPagina(page: number): Promise<void> {
    if (page < 1 || page > this.totalPaginas) return;
    await this.traer(page);
  }

  async aplicarFiltros(): Promise<void> {
    await this.traer(1);
  }

  /**
   * Busca con freno entre teclas, para no pegarle al backend por caracter.
   *
   * El sello descarta respuestas que llegan tarde: sin eso, una consulta lenta
   * de "gui" puede pisar el resultado de "guido" y mostrar filas que no
   * corresponden a lo que dice el campo.
   */
  async onBuscar(): Promise<void> {
    const sello = ++this.selloBusqueda;
    await new Promise((r) => setTimeout(r, 400));
    if (sello !== this.selloBusqueda) return;
    await this.traer(1);
  }

  limpiarFiltros(): void {
    this.filtroEstado = '';
    this.filtroBono = '';
    this.busqueda = '';
    this.traer(1);
  }

  get hayFiltros(): boolean {
    return Boolean(this.filtroEstado || this.filtroBono || this.busqueda.trim());
  }

  private async traer(page: number): Promise<void> {
    this.cargandoTabla = true;
    try {
      const r = await this.llaveService.adminBonos(this.filtros(page));
      this.bonos = r.items;
      this.total = r.total;
      this.page = r.page;
    } catch {
      this.bonos = [];
      this.total = 0;
    } finally {
      this.cargandoTabla = false;
    }
  }

  // -------------------------------------------------- registrados con llave

  async traerUsuarios(page: number): Promise<void> {
    if (page < 1 || page > Math.max(1, this.usuariosPaginas)) return;
    this.cargandoTabla = true;
    try {
      const r = await this.llaveService.usuariosConLlave({
        page,
        limit: this.limit,
        q: this.busquedaUsuarios || undefined,
        reclamo: this.filtroReclamo || undefined,
      });
      this.usuarios = r.items;
      this.usuariosTotal = r.total;
      this.usuariosPage = r.page;
    } catch {
      this.usuarios = [];
      this.usuariosTotal = 0;
    } finally {
      this.cargandoTabla = false;
    }
  }

  async onBuscarUsuarios(): Promise<void> {
    const sello = ++this.selloUsuarios;
    await new Promise((r) => setTimeout(r, 400));
    if (sello !== this.selloUsuarios) return;
    this.usuariosPage = 1;
    await this.traerUsuarios(1);
  }

  async filtrarUsuarios(): Promise<void> {
    this.usuariosPage = 1;
    await this.traerUsuarios(1);
  }

  /**
   * El motivo, en castellano.
   *
   * Existe por la queja concreta que originó la columna: la app decía «tu bono
   * se está liberando» a gente que no iba a cobrar nunca así. Traducirlo acá es
   * lo que convierte esta pantalla en algo que contesta un ticket sin que nadie
   * mire la base.
   */
  motivoLegible(motivo: string | null): string {
    const textos: Record<string, string> = {
      SIN_CAMPANA: 'No había promoción vigente cuando se registró',
      SIN_BONO_ESTE_LADO: 'La campaña premia sólo al otro lado',
      ESPERA_ALTA: 'No terminó el registro',
      ESPERA_KYC: 'Compliance todavía no lo aprobó',
      ESPERA_FONDEO: 'Todavía no cargó su cuenta',
      ESPERA_PRIMER_ENVIO: 'Todavía no hizo su primer envío',
      REFERIDOR_INACTIVO: 'Quien lo invitó no cargó dentro de la ventana',
      CUOTA_AGOTADA: 'Quien lo invitó ya cobró su cuota',
      SIN_PRESUPUESTO: 'La campaña se quedó sin bolsa',
      TECHO_MENSUAL: 'Se alcanzó el techo del mes; cobra el mes que viene',
      MONEDA_SIN_RIEL: 'Esa moneda todavía no se puede acreditar',
      NO_APROBADO: 'Compliance no lo habilita a cobrar',
      VENCIDO: 'Pasó el plazo de reserva sin cumplirse',
      PROGRAMA_APAGADO: 'El programa está apagado',
    };
    return motivo ? (textos[motivo] ?? motivo) : '';
  }

  // -------------------------------------------------------------- rendimiento

  async traerTop(page: number): Promise<void> {
    if (page < 1) return;
    this.cargandoTabla = true;
    try {
      const [top, rendimiento] = await Promise.all([
        this.llaveService.adminTopReferidores(page, this.limit),
        page === 1
          ? this.llaveService.rendimientoCampanas(1, this.limit)
          : Promise.resolve(null),
      ]);
      this.top = top.items;
      this.topTotal = top.total;
      this.topPage = top.page;
      if (rendimiento) this.rendimiento = rendimiento.items;
    } catch {
      this.top = [];
      this.topTotal = 0;
    } finally {
      this.cargandoTabla = false;
    }
  }

  // ------------------------------------------------------------- configuración

  async abrirConfig(): Promise<void> {
    try {
      const c = await this.llaveService.adminConfig();
      this.configOriginal = c;
      this.config = { ...c };
      this.mostrarConfig = true;
    } catch {
      Swal.fire({ text: 'No pudimos leer la configuración.', icon: 'error' });
    }
  }

  cerrarConfig(): void {
    this.mostrarConfig = false;
    this.config = null;
    this.configOriginal = null;
  }

  /** Sólo lo que el operador tocó: el backend escribe únicamente eso. */
  private soloCambios(): Partial<ConfigReferidos> {
    if (!this.config || !this.configOriginal) return {};
    const cambios: any = {};
    for (const k of Object.keys(this.config) as Array<keyof ConfigReferidos>) {
      if (this.config[k] !== this.configOriginal[k]) cambios[k] = this.config[k];
    }
    return cambios;
  }

  get hayCambiosConfig(): boolean {
    return Object.keys(this.soloCambios()).length > 0;
  }

  /** `true` si este guardado enciende el programa. */
  get vaAEncender(): boolean {
    return Boolean(this.config?.activo && !this.configOriginal?.activo);
  }

  async guardarConfig(): Promise<void> {
    const cambios = this.soloCambios();
    if (!Object.keys(cambios).length || this.guardandoConfig) return;

    // Encender el programa arranca los pagos: se confirma aparte, y el texto
    // dice lo que se pierde si se enciende tarde.
    if (this.vaAEncender) {
      const ok = await Swal.fire({
        title: '¿Encender el programa?',
        html:
          'Desde este momento los bonos se empiezan a pagar.<br><br>' +
          'Los invitados que se hayan registrado <b>mientras estaba apagado</b> ' +
          'no quedaron vinculados a nadie: ese período no se recupera.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Encender',
        cancelButtonText: 'Volver',
        confirmButtonColor: '#2F4A2B',
      });
      if (!ok.isConfirmed) return;
    }

    this.guardandoConfig = true;
    try {
      const r = await this.llaveService.adminGuardarConfig(cambios);
      this.cerrarConfig();
      await this.cargar();

      const detalle = r.cambios
        .map((c) => `${c.clave}: ${c.antes} → ${c.ahora}`)
        .join('<br>');
      Swal.fire({
        title: 'Configuración guardada',
        html: detalle || 'Sin cambios.',
        icon: 'success',
        confirmButtonColor: '#2F4A2B',
      });
    } catch (e: any) {
      // El backend valida rangos: un bono de 500 o un piso en 0 vuelven acá.
      const msg = e?.error?.message;
      Swal.fire({
        title: 'No se pudo guardar',
        text: Array.isArray(msg) ? msg.join(' ') : msg || 'Revisá los valores.',
        icon: 'error',
      });
    } finally {
      this.guardandoConfig = false;
    }
  }

  // ------------------------------------------------------------------ ayudas

  /** `true` si los dos bonos del vínculo ya se acreditaron. */
  cobrado(b: BonoAdminItem): boolean {
    return b.bonoReferidor.estado === 'LIBERADO' && b.bonoReferido.estado === 'LIBERADO';
  }

  /**
   * Marca al referidor con muchos invitados y ningún fondeo.
   *
   * Es el patrón de la granja de referidos: cuentas que se registran y nunca
   * dejan un peso. Con el bono atado al fondeo del invitado no cobran, pero
   * igual conviene verlos.
   */
  esSospechoso(r: TopReferidor): boolean {
    return r.invitados >= 5 && r.conFondeo === 0;
  }

  /** Qué acciones admite una campaña según su estado. */
  puede(c: CampanaReferidos, accion: string): boolean {
    switch (accion) {
      case 'activar':
        return c.estado === 'BORRADOR' || c.estado === 'PROGRAMADA';
      case 'pausar':
        return c.estado === 'ACTIVA';
      case 'reanudar':
        return c.estado === 'PAUSADA';
      case 'cerrar':
        return c.estado !== 'CERRADA';
      default:
        return false;
    }
  }
  // -------------------------------------------------------------------------
  // Descargas
  // -------------------------------------------------------------------------

  /**
   * Baja una tabla entera a XLS, no la página que se está viendo.
   *
   * Es la diferencia que importa: un botón que exporta las veinte filas
   * visibles es peor que no tenerlo, porque quien lo aprieta se lleva un
   * archivo que **parece** completo y decide con eso. Así que el export
   * recorre las páginas hasta juntar el total que el servidor declaró.
   *
   * Respeta los filtros de la pantalla: si el operador filtró por «pendientes»,
   * eso es lo que baja. Exportar todo cuando la pantalla muestra un recorte
   * sería la otra forma de mentir.
   *
   * El tope de 20.000 filas no es capricho: más que eso cuelga la pestaña
   * armando la planilla en memoria, y es mejor avisar que quedó cortado que
   * entregar un archivo incompleto en silencio.
   */
  private async descargar<T>(
    clave: string,
    nombre: string,
    traer: (page: number, limit: number) => Promise<Pagina<T>>,
    aFila: (x: T) => Record<string, any>,
  ): Promise<void> {
    if (this.descargando) return;
    this.descargando = clave;

    const POR_PAGINA = 100;
    const TOPE = 20000;

    try {
      const filas: Record<string, any>[] = [];
      let page = 1;
      let total = 0;

      do {
        const r = await traer(page, POR_PAGINA);
        total = r.total;
        filas.push(...r.items.map(aFila));
        page++;
      } while (filas.length < Math.min(total, TOPE) && page <= TOPE / POR_PAGINA);

      if (!filas.length) {
        Swal.fire({ text: 'No hay filas para descargar con esos filtros.', icon: 'info' });
        return;
      }

      this.aXls(nombre, filas);

      if (total > TOPE) {
        Swal.fire({
          title: 'Descarga recortada',
          html:
            `La tabla tiene <b>${total}</b> filas y el archivo trae las primeras ` +
            `<b>${TOPE}</b>.<br><br>Filtrá y volvé a descargar para llevarte el resto.`,
          icon: 'warning',
        });
      }
    } catch (e: any) {
      Swal.fire({
        title: 'No se pudo descargar',
        text: e?.error?.message || 'Intentalo de nuevo.',
        icon: 'error',
      });
    } finally {
      this.descargando = '';
    }
  }

  /** Escribe la planilla. El nombre lleva la fecha para no pisar descargas. */
  private aXls(nombre: string, filas: Record<string, any>[]): void {
    const hoja = XLSX.utils.json_to_sheet(filas);
    const libro = XLSX.utils.book_new();
    // El nombre de la hoja tiene 31 caracteres de tope en Excel; pasarse hace
    // que el archivo no abra.
    XLSX.utils.book_append_sheet(libro, hoja, nombre.slice(0, 31));

    const buffer: ArrayBuffer = XLSX.write(libro, { bookType: 'xlsx', type: 'array' });
    const fecha = new Date().toISOString().slice(0, 10);
    saveAs(
      new Blob([buffer], { type: 'application/octet-stream' }),
      `referidos-${nombre}-${fecha}.xlsx`,
    );
  }

  /** Los montos por moneda, aplanados: una planilla no anida. */
  private montosPlanos(montos: Array<{ moneda: string; cobrado?: number; pendiente?: number; monto?: number; operaciones?: number }>): string {
    return montos
      .map((m) =>
        m.monto !== undefined
          ? `${m.monto} ${m.moneda}`
          : `${m.cobrado ?? 0} cobrado / ${m.pendiente ?? 0} pendiente ${m.moneda}`,
      )
      .join(' · ');
  }

  /**
   * El libro de una campaña: Resumen, Usuarios, Movimientos, Envios y
   * Vinculos, con los totales como fórmulas. Lo arma el backend en el momento,
   * así que siempre trae lo último.
   *
   * La clave de `descargando` lleva el id para que el «Bajando…» aparezca en
   * la fila que se apretó y no en todas.
   */
  async descargarLibroCampana(c: CampanaReferidos): Promise<void> {
    if (this.descargando) return;
    this.descargando = `libro-${c.referidoCampanaId}`;
    try {
      const { archivo, nombre } = await this.llaveService.libroCampana(c.referidoCampanaId);
      saveAs(archivo, nombre);
    } catch (e: any) {
      // Con `responseType: 'blob'` el error también llega como Blob: hay que
      // leerlo para mostrar el motivo real (422, 429) y no uno genérico. El
      // resto de los códigos los aplana a texto el interceptor de errores.
      let msg = typeof e === 'string' ? e : e?.error?.message;
      if (e?.error instanceof Blob) {
        try {
          msg = JSON.parse(await e.error.text())?.message;
        } catch {
          msg = undefined;
        }
      }
      Swal.fire({
        title: 'No se pudo descargar',
        text: Array.isArray(msg) ? msg.join(' ') : msg || 'Intentalo de nuevo.',
        icon: 'error',
      });
    } finally {
      this.descargando = '';
    }
  }

  descargarCampanas(): void {
    this.descargar(
      'campanas',
      'campanas',
      (page, limit) =>
        this.llaveService.campanas({
          page,
          limit,
          estado: this.filtroCampanaEstado || undefined,
          tipo: this.filtroCampanaTipo || undefined,
        }),
      (c) => ({
        Codigo: c.codigo,
        Nombre: c.nombre,
        Tipo: c.tipo,
        Estado: c.estado,
        Prioridad: c.prioridad,
        Alcance: c.alcanceCodigo,
        Gatillo: c.gatilloReferidor,
        'Actividad (dias)': c.actividadReferidorDias,
        Moneda: c.moneda,
        'Bono referidor': c.montoReferidor,
        'Bono invitado': c.montoReferido,
        'Cuota por referidor': c.cuotaPorReferidor ?? 'sin cuota',
        'Cupos usados': c.unidadesUsadas,
        'Cupos totales': c.unidadesTotal,
        'Cupos libres': c.cupoRestante,
        Presupuesto: c.presupuesto,
        Comprometido: c.comprometido,
        Pagado: c.pagado,
        'Bolsa libre': c.bolsaLibre,
        'Reserva (dias)': c.reservaDias,
        'Vigencia desde': c.vigenciaDesde,
        'Vigencia hasta': c.vigenciaHasta ?? 'abierta',
        'Pausada por': c.pausadaMotivo ?? '',
        Referidores: c.referidores,
        'Creada por': c.creadaPorEmail ?? '',
        'Activada por': c.activadaPorEmail ?? '',
      }),
    );
  }

  descargarVinculos(): void {
    this.descargar(
      'vinculos',
      'vinculos',
      (page, limit) => this.llaveService.adminBonos({ ...this.filtros(page), limit }),
      (b) => ({
        Id: b.referidoId,
        Estado: b.estado,
        'Invito (nombre)': b.referidor.nombre,
        'Invito (usuario)': b.llaveReferidor,
        'Invito (correo)': b.referidor.email ?? '',
        'Invitado (nombre)': b.invitado.nombre,
        'Invitado (correo)': b.invitado.email ?? '',
        'Fondeo fecha': b.fondeo?.fecha ?? '',
        'Fondeo moneda': b.fondeo?.moneda ?? '',
        'Bono referidor': b.bonoReferidor.estado,
        'Monto referidor': b.bonoReferidor.montoUsdt,
        'Cobro referidor': b.bonoReferidor.liberadoAt ?? '',
        'Bono invitado': b.bonoReferido.estado,
        'Monto invitado': b.bonoReferido.montoUsdt,
        'Cobro invitado': b.bonoReferido.liberadoAt ?? '',
        Registrado: b.createdAt,
      }),
    );
  }

  descargarUsuariosConLlave(): void {
    this.descargar(
      'registrados',
      'registrados-con-llave',
      (page, limit) =>
        this.llaveService.usuariosConLlave({
          page,
          limit,
          q: this.busquedaUsuarios || undefined,
          reclamo: this.filtroReclamo || undefined,
        }),
      (u) => ({
        Id: u.referidoId,
        Estado: u.estado,
        Invitado: u.invitado.nombre,
        Correo: u.invitado.email ?? '',
        'Usuario Aguacate': u.invitado.llave ?? '',
        'Compliance aprobado': u.invitado.complianceAprobado ? 'si' : 'no',
        'Lo invito': u.referidor.nombre,
        'Usuario de quien invito': u.referidor.llave,
        Campana: u.campana?.codigo ?? 'sin campana',
        'Tipo de campana': u.campana?.tipo ?? '',
        'Cargo monto': u.fondeo?.monto ?? '',
        'Cargo moneda': u.fondeo?.moneda ?? '',
        'Cargo fecha': u.fondeo?.fecha ?? '',
        'Su bono': u.bonoInvitado.estado,
        'Su monto': u.bonoInvitado.monto,
        'Su moneda': u.bonoInvitado.moneda,
        'Por que espera': this.motivoLegible(u.bonoInvitado.motivo),
        Registrado: u.registradoAt,
      }),
    );
  }

  descargarGanancias(): void {
    this.descargar(
      'ganancias',
      'ganancias-por-persona',
      (page, limit) =>
        this.llaveService.usuariosConBono({
          page,
          limit,
          q: this.busquedaGanadores || undefined,
          estado: this.filtroGanancia || undefined,
        }),
      (g) => ({
        'Usuario id': g.usuarioId,
        Nombre: g.nombre,
        Correo: g.email ?? '',
        'Usuario Aguacate': g.llave ?? '',
        'Bonos por invitar': g.comoReferidor,
        'Bonos por ser invitado': g.comoInvitado,
        Cobrados: g.bonos.cobrados,
        Pendientes: g.bonos.pendientes,
        Perdidos: g.bonos.perdidos,
        Montos: this.montosPlanos(g.montos),
        'Por que espera': this.motivoLegible(g.motivoPendiente),
      }),
    );
  }

  descargarInversion(): void {
    this.descargar(
      'inversion',
      'inversion-y-retorno',
      (page, limit) => this.llaveService.inversionRetorno(page, limit),
      (f) => ({
        Codigo: f.codigo,
        Nombre: f.nombre,
        Tipo: f.tipo,
        Estado: f.estado,
        Moneda: f.inversion.moneda,
        Pagado: f.inversion.pagado,
        'Por salir': f.inversion.comprometidoVivo,
        'Costara en total': f.inversion.total,
        Presupuesto: f.inversion.presupuesto,
        Invitados: f.invitados,
        'Invitados que operaron': f.activos,
        Operaciones: f.operaciones,
        'Volumen que movieron': this.montosPlanos(f.volumen),
        'Margen estimado': this.montosPlanos(f.margenEstimado),
        'Margen medible en USD': f.margenUsdMedible,
        'Cobertura %': f.cobertura ?? '',
        Nota: f.coberturaNota ?? '',
      }),
    );
  }

  descargarRendimiento(): void {
    this.descargar(
      'rendimiento',
      'rendimiento-campanas',
      (page, limit) => this.llaveService.rendimientoCampanas(page, limit),
      (r) => ({
        Codigo: r.codigo,
        Nombre: r.nombre,
        Tipo: r.tipo,
        Estado: r.estado,
        Referidores: r.referidores,
        Invitados: r.invitados,
        Cargaron: r.conFondeo,
        'Conversion %': r.conversion,
        Moneda: r.moneda,
        Presupuesto: r.presupuesto,
        Comprometido: r.comprometido,
        Pagado: r.pagado,
        'Costo por activo': r.costoPorActivo ?? 'todavia no se sabe',
      }),
    );
  }

  descargarReferidores(): void {
    this.descargar(
      'referidores',
      'referidores',
      (page, limit) => this.llaveService.adminTopReferidores(page, limit),
      (r) => ({
        'Usuario id': r.usuarioId,
        Nombre: r.nombre ?? '',
        'Usuario Aguacate': r.llave ?? '',
        Invitados: r.invitados,
        Cargaron: r.conFondeo,
        'Bonos cobrados': r.bonosCobrados,
        Cobrado: r.cobradoUsdt,
        Moneda: r.moneda || 'USDT',
      }),
    );
  }

  descargarAsignaciones(): void {
    if (!this.campanaAbierta) return;
    const id = this.campanaAbierta.referidoCampanaId;
    const codigo = this.campanaAbierta.codigo;

    this.descargar(
      'asignaciones',
      `asignados-${codigo}`,
      (page, limit) => this.llaveService.asignaciones(id, page, limit),
      (a) => ({
        'Usuario id': a.usuarioId,
        Nombre: a.nombre,
        Correo: a.email ?? '',
        'Usuario Aguacate': a.llave ?? '',
        Cuota: a.cuota ?? 'la de la campana',
        'Cuota usada': a.cuotaUsada,
        Invitados: a.invitados,
        Cargaron: a.conFondeo,
        Exclusiva: a.exclusiva ? 'si' : 'no',
        Activa: a.activa ? 'si' : 'no',
        Desde: a.desde ?? '',
        Hasta: a.hasta ?? '',
        'Asignada por': a.asignadaPorEmail ?? '',
        'Asignada el': a.asignadaAt,
      }),
    );
  }

}
